import styles from "./index.module.css";
import Loader from "../Loader";
import {
  useEntityInfoMutation,
  useExistingEntityStartedZohoMutation,
  useExistingEntitySubmittedZohoMutation,
  useGetStatesQuery,
} from "../../services/api";
import Select from "react-select";
import CustomInput from "../CustomInput";
import { Controller, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { EntityYesSchema } from "../../yup";
import CustomButton from "../Button/CustomButton";
import { useEffect, useRef, useState } from "react";
import { RootState, useAppDispatch, useAppSelector } from "../../store/store";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faInfoCircle } from "@fortawesome/free-solid-svg-icons";
import { setStage } from "../../store/reducers/stageReducer";
import { setPurlUser } from "../../store/reducers/purlUserReducer";
import { tracker } from "../../services/mixpanel";
interface State {
  _id: string;
  state: string;
}

interface Props {
  onClose: () => void;
}

interface StateOption {
  label: string;
  value: string;
}

const EquityModalNew = ({ onClose }: Props) => {
  const {
    register,
    handleSubmit,
    formState: { errors, isDirty },
    control,
  } = useForm({ resolver: yupResolver(EntityYesSchema) });

  const hasStarted = useRef(false);
  const onError = () => {
    tracker.dashboard.equityContributionExisting.formError();
  };
  useEffect(() => {
    if (isDirty && !hasStarted.current) {
      hasStarted.current = true;
      tracker.dashboard.equityContributionExisting.formStarted();
    }
  }, [isDirty]);
  const dispatch = useAppDispatch();
  const zohoCustomerId = useAppSelector(
    (state: RootState) => state.login.zohoCustomerId
  );
  const stageData = useAppSelector((state) => state.stage);
  const purlUser = useAppSelector((state) => state.purlUser);

  const [stateOptions, setStateOptions] = useState<StateOption[]>();
  const [error, setError] = useState("");
  const { data: states, isLoading } = useGetStatesQuery();
  const [equityResult, { isLoading: equityResultLoading }] =
    useEntityInfoMutation();
  const [entityStarted, { isLoading: entityStartedLoading }] =
    useExistingEntityStartedZohoMutation();
  const [entitySubmitted, { isLoading: entitySubmittedLoading }] =
    useExistingEntitySubmittedZohoMutation();
  const options = [
    { label: "LLC", value: "LLC" },
    { label: "Individual", value: "Individual" },
    { label: "S-Corp", value: "S-Corp" },
    { label: "C-Corp", value: "C-Corp" },
    { label: "Profit Corporation", value: "Profit Corporation" },
    { label: "Other", value: "Other" },
    { label: "Not Applicable", value: "Not Applicable" },
  ];

  const getStates = () => {
    const data = states?.data || [];
    const stateOption: StateOption[] = data.map((item: State) => {
      return {
        label: item.state,
        value: item._id,
      };
    });
    setStateOptions(stateOption);
  };

  useEffect(() => {
    getStates();
  }, [states]);

  const onSubmit = async (values: any) => {
    console.log(values);
    try {
      const formData = {
        zohoCustomerId,
        entityName: values.entityName,
        entityType: values.entityType.value,
        stateOfIncorporation: values.stateOfIncorporation.value,
        businessTitle: values.businessTitle,
        mailingStreet: values.mailingStreet,
        mailingSuite: values.mailingSuite,
        mailingCity: values.mailingCity,
        mailingCountry: "United States",
        mailingState: values.mailingState.value,
        mailingZip: values.mailingZip,
      };

      tracker.dashboard.equityContributionExisting.formSubmitted();
      const data = await equityResult(formData).unwrap();
      console.log(`data.data`, data);
      if (!data.success) {
        setError(data.message || "Something went wrong");
      } else {
        submitEntity();
        dispatch(
          setStage({
            ...stageData,
            entityInformationCollected: true,
          })
        );
      }
      dispatch(setPurlUser({ ...purlUser, entityName: values.entityName }));

      onClose();
    } catch (error) {
      console.log(error);
    }
  };
  const startEntity = async (retries = 3, delay = 2000) => {
    try {
      await entityStarted(zohoCustomerId).unwrap();
    } catch (err) {
      if (retries > 0) {
        setTimeout(() => startEntity(retries - 1, delay), delay);
      } else {
        console.error("entityStarted failed after retries:", err);
      }
    }
  };
  const submitEntity = async (retries = 3, delay = 2000) => {
    try {
      await entitySubmitted(zohoCustomerId).unwrap();
    } catch (err) {
      if (retries > 0) {
        setTimeout(() => startEntity(retries - 1, delay), delay);
      } else {
        console.error("entityStarted failed after retries:", err);
      }
    }
  };
  const handleClose = () => {
    tracker.dashboard.equityContributionExisting.formAbandoned();
    onClose();
  };
  useEffect(() => {
    tracker.dashboard.equityContributionExisting.formStarted();
    startEntity();
  }, []);

  return (
    <div className={styles.overlay} onClick={handleClose}>
      <div
        className={styles.overlayContent2}
        onClick={(e) => e.stopPropagation()}
      >
        {error && (
          <div className="text-red-500 text-2xl text-center">{error}</div>
        )}
        {equityResultLoading && <Loader isLoading={equityResultLoading} />}
        {!equityResultLoading && !error && (
          <div className="h-full">
            <span
              className={`absolute cursor-pointer right-5 top-3 text-4xl`}
              onClick={handleClose}
            >
              &times;
            </span>
            <div className="text-2xl mb-4">Equity Contribution Form</div>
            <form className="w-full" onSubmit={handleSubmit(onSubmit, onError)}>
              <div className="flex items-center w-full py-[6px]">
                <div className="w-[40%] text-md ">
                  Entity Name
                  <FontAwesomeIcon
                    icon={faInfoCircle}
                    className={styles.infoIcon}
                  />
                  <div className={styles.tooltip}>
                    Please enter full legal name for your entity as it would do
                    business
                  </div>
                </div>
                <div className="w-full">
                  <CustomInput
                    name="entityName"
                    register={register}
                    error={errors.entityName}
                    type="text"
                    placeholder="Enter entity name"
                  />
                </div>
              </div>
              <div className="flex items-center w-full py-[6px]">
                <div className="w-[40%] text-md ">Entity Type*</div>
                <div className="w-full">
                  <Controller
                    name="entityType"
                    control={control}
                    render={({ field }: any) => (
                      <Select
                        {...field}
                        placeholder="Select entity type"
                        options={options}
                        onChange={(selectedOption) =>
                          field.onChange(selectedOption)
                        }
                      />
                    )}
                  />
                  {errors.entityType?.message && (
                    <p className="text-red-500 text-[14px] text-left font-semibold">
                      {errors.entityType?.message}
                    </p>
                  )}
                </div>
              </div>
              <div className="flex items-center w-full py-[6px]">
                <div className="w-[40%] text-md ">State of Incorporation*</div>
                <div className="w-full">
                  <Controller
                    name="stateOfIncorporation"
                    control={control}
                    render={({ field }: any) => (
                      <Select
                        {...field}
                        placeholder="Select state of incorporation"
                        isLoading={isLoading}
                        options={stateOptions}
                        onChange={(selectedOption) =>
                          field.onChange(selectedOption)
                        }
                      />
                    )}
                  />
                  {errors.stateOfIncorporation?.message && (
                    <p className="text-red-500 text-[14px] text-left font-semibold">
                      {errors.stateOfIncorporation?.message}
                    </p>
                  )}
                </div>
              </div>
              <div className="flex items-center w-full py-[6px]">
                <div className="w-[40%] text-md ">
                  Business Title
                  <FontAwesomeIcon
                    icon={faInfoCircle}
                    className={styles.infoIcon}
                  />
                  <div className={styles.tooltip}>
                    What is your title in the business? Common entries are
                    Owner, Managing Member and CEO
                  </div>
                </div>
                <div className="w-full">
                  <CustomInput
                    name="businessTitle"
                    register={register}
                    error={errors.businessTitle}
                    type="text"
                    placeholder="Enter business title"
                  />
                </div>
              </div>
              <div className="flex items-center w-full py-[6px]">
                <div className="w-[40%] text-md ">Mailing Street*</div>
                <div className="w-full">
                  <CustomInput
                    name="mailingStreet"
                    register={register}
                    error={errors.mailingStreet}
                    type="text"
                    placeholder="Enter mailing street"
                  />
                </div>
              </div>
              <div className="flex items-center w-full py-[6px]">
                <div className="w-[40%] text-md ">Mailing Suite</div>
                <div className="w-full">
                  <CustomInput
                    name="mailingSuite"
                    register={register}
                    error={errors.mailingSuite}
                    type="text"
                    placeholder="Enter mailing suite"
                  />
                </div>
              </div>
              <div className="flex items-center w-full py-[6px]">
                <div className="w-[40%] text-md ">Mailing Country*</div>
                <div className="w-full pointer-events-none">
                  <CustomInput
                    name="mailingCountry"
                    register={register}
                    type="text"
                    defaultValue="United States"
                    placeholder="Enter mailing country"
                  />
                </div>
              </div>
              <div className="flex items-center w-full py-[6px]">
                <div className="w-[40%] text-md ">Mailing State*</div>
                <div className="w-full">
                  <Controller
                    name="mailingState"
                    control={control}
                    render={({ field }: any) => (
                      <Select
                        {...field}
                        placeholder="Select State"
                        isLoading={isLoading}
                        options={stateOptions}
                        onChange={(selectedOption) =>
                          field.onChange(selectedOption)
                        }
                      />
                    )}
                  />
                  {errors.mailingState?.message && (
                    <p className="text-red-500 text-[14px] text-left font-semibold">
                      {errors.mailingState?.message}
                    </p>
                  )}
                </div>
              </div>
              <div className="flex items-center w-full py-[6px]">
                <div className="w-[40%] text-md ">Mailing City*</div>
                <div className="w-full">
                  <CustomInput
                    name="mailingCity"
                    register={register}
                    error={errors.mailingCity}
                    type="text"
                    placeholder="Enter mailing city"
                  />
                </div>
              </div>
              <div className="flex items-center w-full py-[6px]">
                <div className="w-[40%] text-md ">Mailing Zip*</div>
                <div className="w-full">
                  <CustomInput
                    name="mailingZip"
                    register={register}
                    error={errors.mailingZip}
                    type="text"
                    placeholder="Enter mailing zip"
                  />
                </div>
              </div>
              <div className="flex items-end ml-auto w-[120px] mt-3">
                <CustomButton buttonLabel="SUBMIT" style={{ height: "40px" }} />
              </div>
            </form>
          </div>
        )}
      </div>
    </div>
  );
};

export default EquityModalNew;
